import * as THREE from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { buildModel } from './buildModel'
import { decodeImageBlob } from './loadSkinTexture'
import type { RenderRecipe } from './types'

const IN_TO_M = 0.0254
const REAL_W_M = 8.5 * IN_TO_M // book trim width
const REAL_H_M = 11 * IN_TO_M // book trim height

export interface ExportOptions {
  recipe: RenderRecipe
  /** Per-type orientation (degrees) + Euler order — same as the live viewer. */
  rotationDeg: [number, number, number]
  order: THREE.EulerOrder
  /** Softcover spine thickness multiplier (1 for hardcover/snapbook). */
  thickness: number
  /** Cover artwork URL. */
  skinUrl: string
  /** Texture size cap (≤2048 for Quick Look). */
  maxSize: number
  /** Bake a vertical flip into the cover image. */
  flipV: boolean
  /** Bake a horizontal flip into the cover image. */
  flipH: boolean
}

/**
 * Build the cover artwork as a plain texture with the flips BAKED into the pixels
 * (glTF/USDZ can't rely on three's flipY or negative texture transforms). flipY is
 * forced false so model-viewer / Quick Look read the image as stored.
 */
async function loadBakedTexture(url: string, maxSize: number, flipV: boolean, flipH: boolean): Promise<THREE.Texture> {
  const res = await fetch(url, { mode: 'cors' })
  if (!res.ok) throw new Error(`Failed to fetch skin (${res.status})`)
  const decoded = await decodeImageBlob(await res.blob())
  const scale = Math.min(1, maxSize / Math.max(decoded.width, decoded.height))
  const w = Math.max(1, Math.round(decoded.width * scale))
  const h = Math.max(1, Math.round(decoded.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingQuality = 'high'
  ctx.translate(flipH ? w : 0, flipV ? h : 0)
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1)
  ctx.drawImage(decoded.source, 0, 0, w, h)
  decoded.release()
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.flipY = false
  tex.needsUpdate = true
  return tex
}

/**
 * Produce a self-contained GLB of the book — cover baked in, oriented upright, grounded
 * on y=0, and scaled to real 8.5"×11". Handed to <model-viewer>, which generates the
 * USDZ for iOS Quick Look on the fly (no server needed for iOS).
 */
export async function exportBookGlb(opts: ExportOptions): Promise<Blob> {
  const built = buildModel(opts.recipe, opts.thickness)
  const tex = await loadBakedTexture(opts.skinUrl, opts.maxSize, opts.flipV, opts.flipH)

  // Convert to MeshStandard so the GLB exports clean PBR (and looks right under AR lighting).
  const coverSet = new Set<THREE.Material>(built.coverMaterials)
  const toStandard = (m: THREE.Material): THREE.MeshStandardMaterial => {
    const isCover = coverSet.has(m)
    return new THREE.MeshStandardMaterial({
      map: isCover ? tex : null,
      color: new THREE.Color(isCover ? 0xffffff : 0xf2f2f2),
      roughness: isCover ? 0.5 : 0.9,
      metalness: 0,
      side: THREE.DoubleSide,
    })
  }
  built.group.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh) return
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(toStandard) : toStandard(mesh.material)
  })

  // Orient (per-type), then recenter so the base sits on y=0, centered in x/z.
  const inner = new THREE.Group()
  inner.rotation.set(
    THREE.MathUtils.degToRad(opts.rotationDeg[0]),
    THREE.MathUtils.degToRad(opts.rotationDeg[1]),
    THREE.MathUtils.degToRad(opts.rotationDeg[2]),
    opts.order,
  )
  inner.add(built.group)
  const root = new THREE.Group()
  root.add(inner)
  root.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(root)
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())
  inner.position.set(-center.x, -box.min.y, -center.z)

  // Real-world scale: front cover exactly 8.5"×11"; thickness proportional to height.
  const sx = REAL_W_M / (size.x || 1)
  const sy = REAL_H_M / (size.y || 1)
  root.scale.set(sx, sy, sy)
  root.updateMatrixWorld(true)

  const exporter = new GLTFExporter()
  const glb = (await exporter.parseAsync(root, { binary: true, onlyVisible: true })) as ArrayBuffer

  // Dispose the throwaway scene (the GLB already embeds the data).
  built.group.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh) return
    mesh.geometry?.dispose()
    ;(Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach((m) => m.dispose())
  })
  built.coverMaterials.forEach((m) => m.dispose())
  tex.dispose()

  return new Blob([glb], { type: 'model/gltf-binary' })
}
